import { Routes, Route, Navigate } from 'react-router-dom';
import { TenantListPage } from './pages/TenantListPage';
import { TenantDetailPage } from './pages/TenantDetailPage';
import { CreateTenantWizard } from './pages/CreateTenantWizard';
import { AuditLogPage } from './pages/AuditLogPage';
import { AwxJobsPage } from './pages/AwxJobsPage';
import { CtiDeploymentsPage } from './pages/CtiDeploymentsPage';
import { CreateCtiDeployment } from './pages/CreateCtiDeployment';
import { FederationTargetsPage } from './pages/FederationTargetsPage';
import { PlatformCredentialsPage } from './pages/PlatformCredentialsPage';
import {
  MarketplaceFleetPage,
  MarketplaceFleetNavBadge,
} from './pages/MarketplaceFleetPage';
import {
  ServiceVersionsPage,
  ServiceVersionsNavBadge,
} from './pages/ServiceVersionsPage';
import {
  PlanChangeRequestsPage,
  PlanChangeRequestsNavBadge,
} from './pages/PlanChangeRequestsPage';
import { OrdersPage, OrdersNavBadge } from './pages/OrdersPage';
import { ContextsPage } from './pages/ContextsPage';
import { CustomersListPage } from './pages/CustomersListPage';
import { CustomerDetailPage } from './pages/CustomerDetailPage';
import { ContractsOverviewPage } from './pages/ContractsOverviewPage';
import { BrandsPage } from './pages/BrandsPage';
import { AppRequestsPage, AppRequestsNavBadge } from './pages/AppRequestsPage';
import { AppMarketplacePage } from './pages/AppMarketplacePage';
import { BillingOverviewPage } from './pages/BillingOverviewPage';
import { PriceListsPage } from './pages/PriceListsPage';
import { PriceListDetailPage } from './pages/PriceListDetailPage';
import { BillingStatementsPage } from './pages/BillingStatementsPage';
import { BillingStatementDetailPage } from './pages/BillingStatementDetailPage';
import { PlatformMessagesPage } from './pages/PlatformMessagesPage';
import { AuthGuard } from './components/AuthGuard';
import { useAuth } from './contexts/AuthContext';
import { useBrand } from './contexts/BrandContext';
import { AdminShell } from './shell/AdminShell';

export default function App() {
  const { operator, loading, signIn } = useAuth();
  const { brand } = useBrand();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100 text-slate-500">
        Lade…
      </div>
    );
  }
  if (!operator) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="bg-white rounded shadow p-8 max-w-md text-center">
          <h1 className="text-2xl font-brand text-neutral-900 mb-2">{brand.name} Platform Admin</h1>
          <p className="text-slate-600 mb-4">Operator-only console — Anmeldung erforderlich.</p>
          <button onClick={signIn} className="btn-primary w-full">Mit Keycloak anmelden</button>
        </div>
      </div>
    );
  }

  // Nav-Badges (Plan-Aenderungen, Marketplace) wandern aus der alten Top-Nav in
  // die AppShell-Sidebar. Die Badge-Komponenten pollen selbst (alle 30s).
  const navBadges = {
    'plan-changes': <PlanChangeRequestsNavBadge />,
    'orders': <OrdersNavBadge />,
    'app-requests': <AppRequestsNavBadge />,
    'marketplace-fleet': <MarketplaceFleetNavBadge />,
    'service-versions': <ServiceVersionsNavBadge />,
  };

  return (
    <AdminShell navBadges={navBadges}>
      <div className="p-6">
        <AuthGuard>
          <Routes>
            <Route path="/" element={<Navigate to="/tenants" replace />} />
            <Route path="/tenants" element={<TenantListPage />} />
            <Route path="/tenants/new" element={<CreateTenantWizard />} />
            <Route path="/tenants/:slug" element={<TenantDetailPage />} />
            <Route path="/messages" element={<PlatformMessagesPage />} />
            <Route path="/customers" element={<CustomersListPage />} />
            <Route path="/customers/:id" element={<CustomerDetailPage />} />
            <Route path="/contracts" element={<ContractsOverviewPage />} />
            <Route path="/billing" element={<BillingOverviewPage />} />
            <Route path="/billing/price-lists" element={<PriceListsPage />} />
            <Route path="/billing/price-lists/:id" element={<PriceListDetailPage />} />
            <Route path="/billing/statements" element={<BillingStatementsPage />} />
            <Route path="/billing/statements/:id" element={<BillingStatementDetailPage />} />
            <Route path="/contexts" element={<ContextsPage />} />
            <Route path="/brands" element={<BrandsPage />} />
            <Route path="/plan-changes" element={<PlanChangeRequestsPage />} />
            <Route path="/orders" element={<OrdersPage />} />
            <Route path="/app-requests" element={<AppRequestsPage />} />
            <Route path="/app-marketplace" element={<AppMarketplacePage />} />
            <Route path="/audit" element={<AuditLogPage />} />
            <Route path="/jobs" element={<AwxJobsPage />} />
            <Route path="/cti-deployments" element={<CtiDeploymentsPage />} />
            <Route path="/cti-deployments/new" element={<CreateCtiDeployment />} />
            <Route path="/federation" element={<FederationTargetsPage />} />
            <Route path="/credentials" element={<PlatformCredentialsPage />} />
            <Route path="/marketplace-fleet" element={<MarketplaceFleetPage />} />
            <Route path="/service-versions" element={<ServiceVersionsPage />} />
          </Routes>
        </AuthGuard>
      </div>
    </AdminShell>
  );
}
